import type React from 'react';

type TStringPath = string[];

interface ICascaderMenuOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
  children?: ICascaderMenuOption[];
}

export interface IAbTestDisplayGroup {
  level1Label: string;
  level2Label: string;
  level3Labels: string[];
}

export const buildAbTestDisplayGroups = (
  data: ICascaderMenuOption[],
  paths: TStringPath[],
): IAbTestDisplayGroup[] => {
  if (!paths?.length) return [];

  const groups: Record<string, IAbTestDisplayGroup> = {};

  const getLabelsByPath = (path: TStringPath): string[] => {
    const labels: string[] = [];
    let currentLevel: ICascaderMenuOption[] | undefined = data;

    path.forEach((value) => {
      if (!currentLevel) return;
      const node = currentLevel.find((item) => item.value === value);
      if (!node) return;
      labels.push(
        typeof node.label === 'string' || typeof node.label === 'number'
          ? String(node.label)
          : String(node.value),
      );
      currentLevel = node.children;
    });

    return labels;
  };

  paths.forEach((path) => {
    if (!Array.isArray(path) || path.length < 2) return;
    const [level1, level2] = path;
    if (!level1 || !level2) return;

    const labels = getLabelsByPath(path);
    if (labels.length < 2) return;

    const [level1Label, level2Label, ...restLabels] = labels;
    const key = `${level1}|${level2}`;
    if (!groups[key]) {
      groups[key] = {
        level1Label,
        level2Label,
        level3Labels: [],
      };
    }
    if (restLabels.length) {
      groups[key].level3Labels.push(...restLabels);
    }
  });

  const result: IAbTestDisplayGroup[] = [];
  Object.values(groups).forEach((group) => {
    const uniqueLevel3 = Array.from(new Set(group.level3Labels));
    result.push({
      level1Label: group.level1Label,
      level2Label: group.level2Label,
      level3Labels: uniqueLevel3,
    });
  });

  return result;
};

export const buildAbTestCascaderDisplayText = (
  data: ICascaderMenuOption[],
  paths: TStringPath[],
): string => {
  const groups = buildAbTestDisplayGroups(data, paths);
  if (!groups.length) return '';

  const parts: string[] = [];
  groups.forEach((group) => {
    if (group.level3Labels.length) {
      parts.push(
        `${group.level1Label}:${group.level2Label}(${group.level3Labels.join(', ')})`,
      );
    } else {
      parts.push(`${group.level1Label}:${group.level2Label}`);
    }
  });

  return parts.join('；');
};

/**
 * ABTestCascader 的默认选中值回显渲染逻辑。
 * 说明：直接返回可展示的回显内容。
 */
export const renderAbTestCascaderValue = (
  data: ICascaderMenuOption[],
  value: TStringPath[],
  _selectedOptions: any[],
) => {
  const text = buildAbTestCascaderDisplayText(data, value);
  if (!text) {
    return null;
  }
  return text;
};

export default renderAbTestCascaderValue;

